/**
 * Sitemap Utilities
 * Configuration-based sitemap generation with multi-locale support
 */

import { getCanonicalUrl, getLanguageAlternates } from '../url'
import type { SiteConfig } from '../url/types'

export type ChangeFreq = 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never'

export interface SitemapRoute {
  url: string
  lastModified?: string | Date
  changeFrequency?: ChangeFreq
  priority?: number
  alternates?: {
    languages?: Record<string, string>
  }
}

/**
 * Calculate dynamic priority and changefreq based on content age
 */
export function calculateContentPriority(modifiedDate: string | Date): {
  priority: number
  changefreq: ChangeFreq
} {
  const now = new Date()
  const modified = new Date(modifiedDate)
  const daysSinceModified = Math.floor((now.getTime() - modified.getTime()) / (1000 * 60 * 60 * 24))

  if (daysSinceModified <= 7) {
    return { priority: 0.9, changefreq: 'daily' }
  }

  if (daysSinceModified <= 30) {
    return { priority: 0.8, changefreq: 'weekly' }
  }

  if (daysSinceModified <= 90) {
    return { priority: 0.7, changefreq: 'monthly' }
  }

  if (daysSinceModified <= 180) {
    return { priority: 0.6, changefreq: 'monthly' }
  }

  return { priority: 0.5, changefreq: 'monthly' }
}

/**
 * Generate sitemap URL entry with language alternates
 */
export function generateSitemapUrl(
  path: string,
  config: SiteConfig,
  modifiedDate?: string | Date,
  overrides?: Partial<SitemapRoute>
): SitemapRoute {
  const url = getCanonicalUrl(path, config)
  const alternates = getLanguageAlternates(path, config)

  // Calculate priority and changefreq based on modified date if provided
  let priority = 0.8
  let changeFrequency: ChangeFreq = 'monthly'

  if (modifiedDate) {
    const calculated = calculateContentPriority(modifiedDate)
    priority = calculated.priority
    changeFrequency = calculated.changefreq
  }

  return {
    url,
    lastModified: modifiedDate,
    changeFrequency,
    priority,
    alternates: {
      languages: alternates,
    },
    ...overrides,
  }
}

/**
 * Sort sitemap URLs by priority (high to low) and lastModified (recent to old)
 */
export function sortSitemapUrls(urls: SitemapRoute[]): SitemapRoute[] {
  return urls.sort((a, b) => {
    // Sort by priority first (descending)
    const priorityA = a.priority ?? 0
    const priorityB = b.priority ?? 0

    if (priorityA !== priorityB) {
      return priorityB - priorityA
    }

    // Then by date (most recent first)
    if (a.lastModified && b.lastModified) {
      const dateA = new Date(a.lastModified).getTime()
      const dateB = new Date(b.lastModified).getTime()
      return dateB - dateA
    }

    // If one has lastModified and the other doesn't, prefer the one with it
    if (a.lastModified) return -1
    if (b.lastModified) return 1

    return 0
  })
}

/**
 * Validate sitemap URL entry
 */
export function validateSitemapUrl(route: SitemapRoute): boolean {
  // Validate URL
  try {
    const parsed = new URL(route.url)
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false
    }
  } catch {
    return false
  }

  // Validate priority if provided
  if (route.priority !== undefined) {
    if (route.priority < 0 || route.priority > 1) {
      return false
    }
  }

  // Validate changeFrequency if provided
  if (route.changeFrequency) {
    const validFreqs: ChangeFreq[] = ['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never']
    if (!validFreqs.includes(route.changeFrequency)) {
      return false
    }
  }

  return true
}

/**
 * Split sitemap into chunks (Google recommends max 50,000 URLs)
 */
export function splitSitemapUrls<T>(
  urls: T[],
  chunkSize: number = 50000
): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < urls.length; i += chunkSize) {
    chunks.push(urls.slice(i, i + chunkSize))
  }
  return chunks
}
